import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Accordion,
  AccordionItem,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  getKeyValue,
} from "@nextui-org/react";
import { faChartColumn, faTable } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactApexChart from "react-apexcharts";

import { usePost } from "../../api/reactQueryCustomHooks.jsx";
import { apiRoute } from "../../constants/enum.js";
import LoadingCard from "../../components/LoadingCard.jsx";
import { getMonthNamesBetween } from "../../Utility/utility.js";


const DashboardChart = ({ propDate }) => {
  const { compCode, yearId } = useSelector((state) => state.company);

  const [sale, setSale] = useState([]);
  const [purchase, setPurchase] = useState([]);
  const [chartXAxis, setChartXAxis] = useState([]);

  // ✅ Build Month → Value mapping
const normalize = (m) => m.toLowerCase().substring(0, 3);

const buildChartData = (data, chartXAxis) => {
  const map = {};

  data.forEach((item) => {
    map[normalize(item.Mnth)] = Number(item.Total_Value);
  });

  return chartXAxis.map((month) => {
    return map[normalize(month)] || 0;
  });
};

  const columns = [
    { key: "year", label: "Year" },
    { key: "Mnth", label: "Month" },
    { key: "Total_Value", label: "Total Value", align: "right" },
  ];

  const apexChartOptions = {
    chart: {
      type: "bar",
      stacked: false,
      width: "100%",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "60%",
      },
    },
    xaxis: {
      categories: chartXAxis,
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: (val) => (val ? val.toFixed(0) : "0"),
      },
    },
    dataLabels: {
      enabled: false,
    },
    colors: ["#4caf50", "#f9ce1d"],
    stroke: {
      show: false,
    },
  };

  const SimpleTable = ({ rows, columns, id }) => {
    let index = 0;
    return (
      <Table shadow="none">
        <TableHeader columns={columns}>
          {(column) => (
            <TableColumn
              key={column.key}
              className={column.align ? "text-right" : ""}
            >
              {column.label}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody items={rows}>
          {(item) => {
            index++;
            return (
              <TableRow key={item[id] || index}>
                {(columnKey) => {
                  const column = columns.find((c) => c.key === columnKey);
                  return (
                    <TableCell className={column?.align ? "text-right" : ""}>
                      {getKeyValue(item, columnKey)}
                    </TableCell>
                  );
                }}
              </TableRow>
            );
          }}
        </TableBody>
      </Table>
    );
  };

  const { isLoading, mutate: loadPurchase } = usePost({
    url: apiRoute.dashboard.getMultiYearTradingData,
    queryKey: "PurchaseChart",
    onSuccessTasks: (data) => setPurchase(data || []),
  });

  const { data: saleData = [], mutate: loadSale } = usePost({
    url: apiRoute.dashboard.getMultiYearTradingData,
    queryKey: "SaleChart",
    onSuccessTasks: (data) => setSale(data || []),
  });

  useEffect(() => {
    const months = getMonthNamesBetween(propDate.start, propDate.end);
    setChartXAxis(months || []);

    const payload = {
      compcode: compCode.join(","),
      fromdate: propDate.start,
      todate: propDate.end,
      db_inilchar: yearId
        ? yearId.substring(0, yearId.length - 4)
        : "ACC",
    };

    loadPurchase({ data: { ...payload, action: "Purchase" } });
    loadSale({ data: { ...payload, action: "Sale" } });
  }, [propDate]);

  const saleChart = useMemo(() => {
    return buildChartData(saleData, chartXAxis);
  }, [sale, chartXAxis]);

  const purchaseChart = useMemo(() => {
    return buildChartData(purchase, chartXAxis);
  }, [purchase, chartXAxis]);

  const apexChartSeries = [
    { name: "Purchase", data: purchaseChart },
    { name: "Sale", data: saleChart },
  ];

  // ✅ Debug (remove later)


  if (isLoading) {
    return <LoadingCard />;
  }

  if (!chartXAxis.length) {
    return <div>No data available</div>;
  }

  return (
    <Accordion variant="splitted" defaultExpandedKeys={["1"]}>
      <AccordionItem
        key="1"
        title="Sale Chart"
        startContent={<FontAwesomeIcon icon={faChartColumn} />}
      >
        <div className="h-[70vh]">
          <ReactApexChart
            key={JSON.stringify(chartXAxis)} // ✅ force refresh
            options={apexChartOptions}
            series={apexChartSeries}
            type="bar"
            height="100%"
          />
        </div>
      </AccordionItem>

      <AccordionItem
        key="2"
        title="Trading Table"
        startContent={<FontAwesomeIcon icon={faTable} />}
      >
        <Accordion variant="splitted">
          <AccordionItem key="p" title="Purchase">
            <SimpleTable rows={purchase} columns={columns} id="Month" />
          </AccordionItem>

          <AccordionItem key="s" title="Sale">
            <SimpleTable rows={sale} columns={columns} id="Month" />
          </AccordionItem>
        </Accordion>
      </AccordionItem>
    </Accordion>
  );
};

export default DashboardChart;